// ===========================================
// Custom Emails Management Page
// ===========================================
// Admin interface for managing custom email campaigns

import { useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { apiErrorMessage } from '../../utils/apiError';
import { useTenant } from '../../contexts/TenantContext';
import {
  Box,
  TextField,
  Alert,
  Chip,
  Button,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Menu,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  // The edit/view icon is rendered by `renderExtraActions`, NOT by DataTable's
  // `onEdit` — the icon has to vary per row (pencil for a DRAFT, eye for a SENT
  // record) and DataTable's is fixed per table. `onEdit` is deliberately NOT
  // passed, or the table would render a second, always-pencil icon beside it.
  Edit as EditIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
  ContentCopy as DuplicateIcon,
  Send as SendIcon,
  Schedule as ScheduleIcon,
  MoreVert as MoreIcon,
  Analytics as AnalyticsIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import PageHeader from '../../components/common/PageHeader';
import DataTable, { Column } from '../../components/common/DataTable';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import {
  useCustomEmails,
  useDeleteCustomEmail,
  useDuplicateCustomEmail,
  useSendCustomEmail,
  useRecipientPreview,
  useCancelScheduledEmail,
} from '../../hooks/useCustomEmail';
import {
  CustomEmailListItem,
  CustomEmailStatus,
  CustomEmailFilters,
} from '../../types/customEmail.types';

// ===========================================
// STATUS DISPLAY
// ===========================================

const statusColors: Record<
  CustomEmailStatus,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  DRAFT: 'default',
  SCHEDULED: 'info',
  SENDING: 'warning',
  SENT: 'success',
  FAILED: 'error',
};

const statusLabels: Record<CustomEmailStatus, string> = {
  DRAFT: 'Draft',
  SCHEDULED: 'Scheduled',
  SENDING: 'Sending',
  SENT: 'Sent',
  FAILED: 'Failed',
};

/**
 * A SENDING row is not necessarily sending. The campaign is claimed and
 * enqueued the moment the admin presses Send, but a worker may not pick it up
 * for a while — one campaign runs at a time and one can take hours. Showing
 * "Sending" then tells the admin delivery is under way when it has not
 * started. `sendState` comes from the backend's live queue lookup — the same
 * one the reaper uses to decide a row is queued rather than dead.
 */
export function sendStatusLabel(row: Pick<CustomEmailListItem, 'status' | 'sendState'>): string {
  if (row.status !== 'SENDING') return statusLabels[row.status];
  return row.sendState === 'QUEUED' ? 'Queued' : 'Sending';
}

// ===========================================
// ENGAGEMENT (opens / clicks)
// ===========================================
// Open + click tracking was switched on for email.musicnme.com.au on
// 2026-09-10. Campaigns sent BEFORE that date generated no Mailgun events and
// can never be backfilled, so their counts are absent, not zero. Rendering them
// `0 (0%)` would read as "nobody opened it" — a much worse lie than `-`.
const ENGAGEMENT_TRACKING_START = new Date('2026-09-10T00:00:00Z');
/** dd/mm/yyyy, en-AU (Critical Rule 7). */
const ENGAGEMENT_TRACKING_START_LABEL = '10/09/2026';

export function engagementCell(count: number, row: CustomEmailListItem): string {
  const sent = row.sentCount;
  if (!sent) return '-';
  // Sent before tracking existed: we have no data, and never will.
  if (row.sentAt && new Date(row.sentAt) < ENGAGEMENT_TRACKING_START) return '-';
  const rate = Math.round((count / sent) * 100);
  return `${count} (${rate}%)`;
}

// ===========================================
// COMPONENT
// ===========================================

export default function CustomEmailsPage() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { schoolSlug } = useTenant();

  // Filter state
  const [filters, setFilters] = useState<CustomEmailFilters>({
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Context menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedEmail, setSelectedEmail] = useState<CustomEmailListItem | null>(null);

  // Delete confirmation state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [emailToDelete, setEmailToDelete] = useState<CustomEmailListItem | null>(null);

  // Send confirmation state
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [emailToSend, setEmailToSend] = useState<CustomEmailListItem | null>(null);

  // Cancel confirmation state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [emailToCancel, setEmailToCancel] = useState<CustomEmailListItem | null>(null);

  // Queries and mutations
  const { data: customEmailsData, isLoading, error } = useCustomEmails({
    ...filters,
    search: searchTerm || undefined,
  });
  const deleteMutation = useDeleteCustomEmail();
  const duplicateMutation = useDuplicateCustomEmail();
  const sendMutation = useSendCustomEmail();

  // Live recipient count for the send confirmation.
  //
  // `emailToSend.totalRecipients` is a STORED column written only after a send
  // completes, so on a DRAFT it is always 0. The dialog has to resolve the real
  // audience the same way the editor page does. The hook is `enabled`-guarded on
  // the id, so this fires only once a campaign is actually queued for sending.
  const {
    data: sendPreview,
    isLoading: isLoadingSendPreview,
    isError: sendPreviewError,
  } = useRecipientPreview(emailToSend?.id ?? '');
  const cancelMutation = useCancelScheduledEmail();

  const customEmails = useMemo(() => customEmailsData?.data || [], [customEmailsData]);

  // Table columns
  const columns: Column<CustomEmailListItem>[] = [
    {
      id: 'title',
      label: 'Title',
      minWidth: 200,
      format: (value, row) => (
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {value as string}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ maxWidth: 250, display: 'block' }}>
            {row.subject}
          </Typography>
        </Box>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      // ⚠️ Takes the ROW, not just the value: a SENDING row may not have
      // started yet, and `sendStatusLabel` is what turns that into "Queued".
      // Reading `value` alone renders the raw status and silently discards
      // the whole queued-visibility feature (/qa Phase 5, CRITICAL-1).
      format: (_value, row) => (
        <Chip
          label={sendStatusLabel(row)}
          size="small"
          color={statusColors[row.status]}
        />
      ),
    },
    {
      id: 'author',
      label: 'Author',
      minWidth: 130,
      format: (_, row) => `${row.author.firstName} ${row.author.lastName}`,
    },
    {
      id: 'totalRecipients',
      label: 'Recipients',
      minWidth: 100,
      align: 'right',
      format: (value) => (value as number) || '-',
    },
    {
      id: 'sentCount',
      label: 'Sent',
      minWidth: 80,
      align: 'right',
      format: (value, row) => {
        const sent = value as number;
        const total = row.totalRecipients;
        return total > 0 ? `${sent}/${total}` : '-';
      },
    },
    {
      id: 'openedCount',
      label: 'Opens',
      minWidth: 90,
      align: 'right',
      format: (value, row) => engagementCell(value as number, row),
    },
    {
      id: 'clickedCount',
      label: 'Clicks',
      minWidth: 90,
      align: 'right',
      format: (value, row) => engagementCell(value as number, row),
    },
    {
      id: 'scheduledFor',
      label: 'Scheduled',
      minWidth: 120,
      format: (value) =>
        value ? new Date(value as string).toLocaleString() : '-',
    },
    {
      id: 'createdAt',
      label: 'Created',
      minWidth: 100,
      format: (value) => new Date(value as string).toLocaleDateString(),
    },
  ];

  // Handlers
  const handleEdit = (email: CustomEmailListItem) => {
    navigate(`/${schoolSlug}/admin/custom-emails/${email.id}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, email: CustomEmailListItem) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setSelectedEmail(email);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedEmail(null);
  };

  const handleDuplicate = async () => {
    if (!selectedEmail) return;
    try {
      await duplicateMutation.mutateAsync(selectedEmail.id);
    } catch (err) {
      // ⚠️ Duplicate can now 422. Duplicating a campaign that predates
      // merge-field validation fails with a message NAMING the bad token, and a
      // console-only error leaves the admin clicking a menu item that silently
      // does nothing. This page has no error banner at all, so the snackbar is
      // the only surface available.
      console.error('Failed to duplicate custom email:', err);
      enqueueSnackbar(`Could not duplicate: ${apiErrorMessage(err)}`, { variant: 'error' });
    }
    handleMenuClose();
  };

  const handleSendClick = () => {
    if (!selectedEmail) return;
    setEmailToSend(selectedEmail);
    setSendDialogOpen(true);
    handleMenuClose();
  };

  const handleSendConfirm = async () => {
    if (!emailToSend) return;
    try {
      await sendMutation.mutateAsync(emailToSend.id);
      setSendDialogOpen(false);
      setEmailToSend(null);
    } catch (err) {
      // Send re-validates merge fields, so a legacy campaign fails here with a
      // 422 naming the field. Surface it — the dialog stays open on failure and
      // would otherwise look simply broken.
      console.error('Failed to send custom email:', err);
      enqueueSnackbar(`Could not send: ${apiErrorMessage(err)}`, { variant: 'error' });
    }
  };

  const handleCancelClick = () => {
    if (!selectedEmail) return;
    setEmailToCancel(selectedEmail);
    setCancelDialogOpen(true);
    handleMenuClose();
  };

  const handleCancelConfirm = async () => {
    if (!emailToCancel) return;
    try {
      await cancelMutation.mutateAsync(emailToCancel.id);
      setCancelDialogOpen(false);
      setEmailToCancel(null);
    } catch (err) {
      console.error('Failed to cancel scheduled email:', err);
    }
  };

  const handleDeleteClick = () => {
    if (!selectedEmail) return;
    setEmailToDelete(selectedEmail);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!emailToDelete) return;
    try {
      await deleteMutation.mutateAsync(emailToDelete.id);
      setDeleteDialogOpen(false);
      setEmailToDelete(null);
    } catch (err) {
      console.error('Failed to delete custom email:', err);
    }
  };

  /**
   * Extra row actions, rendered INSIDE DataTable's own actions cell.
   *
   * ⚠️ Do NOT append a second column to `columns` for this. DataTable renders
   * its own actions column whenever `onEdit`/`onDelete`/`onView`/
   * `renderExtraActions` is supplied, so appending one produced TWO actions
   * columns — the duplicate pushed the real controls out of view and left the
   * page looking like it had no edit or send actions at all. That is what MNM
   * reported. `renderExtraActions` is the established pattern (see
   * StudentsPage).
   *
   * The "More actions" menu is where Send Now / Schedule / Cancel / Analytics /
   * Delete live, gated by status.
   */
  const renderExtraActions = (row: CustomEmailListItem) => {
    // A SENT or SENDING campaign is an immutable record — the editor shows it
    // read-only. Showing a pencil promises an edit that cannot happen, so the
    // icon and tooltip change to match. DataTable's own `onEdit` icon is fixed
    // per-table, so the row-aware version lives here instead.
    const isImmutable = row.status === 'SENT' || row.status === 'SENDING';

    return (
      <>
        <Tooltip title={isImmutable ? 'View' : 'Edit'}>
          <IconButton size="small" onClick={() => handleEdit(row)}>
            {isImmutable ? <ViewIcon fontSize="small" /> : <EditIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="More actions">
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, row)}>
            <MoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </>
    );
  };

  return (
    <Box>
      <PageHeader
        title="Custom Emails"
        subtitle="Create and send targeted email campaigns"
        action={
          <Button variant="contained" startIcon={<AddIcon />} component={Link} to={`/${schoolSlug}/admin/custom-emails/new`}>
            Create Email
          </Button>
        }
      />

      {/* Filters */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }} alignItems="center">
        <TextField
          label="Search"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
          placeholder="Search by title or subject..."
        />
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={filters.status || ''}
            label="Status"
            onChange={(e) =>
              setFilters({ ...filters, status: (e.target.value as CustomEmailStatus) || undefined })
            }
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="DRAFT">Draft</MenuItem>
            <MenuItem value="SCHEDULED">Scheduled</MenuItem>
            <MenuItem value="SENDING">Sending</MenuItem>
            <MenuItem value="SENT">Sent</MenuItem>
            <MenuItem value="FAILED">Failed</MenuItem>
          </Select>
        </FormControl>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Failed to load custom emails: {error instanceof Error ? error.message : 'Unknown error'}
        </Alert>
      )}

      <DataTable<CustomEmailListItem>
        columns={columns}
        data={customEmails}
        loading={isLoading}
        renderExtraActions={renderExtraActions}
        emptyMessage="No custom emails found. Create your first email campaign to get started."
      />

      {/* Without this, a historical campaign showing "-" reads as a bug, and a
          "0 (0%)" would read as "nobody opened it". Neither is true: there is
          simply no data before tracking was switched on. */}
      {customEmails.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Open and click tracking began {ENGAGEMENT_TRACKING_START_LABEL}. Campaigns sent before
          then show no engagement data. Opens are approximate — some email apps block the
          tracking pixel, and others load it automatically.
        </Typography>
      )}

      {/* Context Menu */}
      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem onClick={handleDuplicate}>
          <DuplicateIcon fontSize="small" sx={{ mr: 1 }} /> Duplicate
        </MenuItem>
        {/* FAILED is retryable, not a dead end. The backend skips recipients
            already marked delivered, so pressing Send on a partially-delivered
            campaign retries ONLY the ones who never received it — nobody is
            emailed twice. Without this the resume path exists server-side but
            is unreachable from the UI. */}
        {(selectedEmail?.status === 'DRAFT' || selectedEmail?.status === 'FAILED') && (
          <MenuItem onClick={handleSendClick}>
            <SendIcon fontSize="small" sx={{ mr: 1 }} />{' '}
            {selectedEmail?.status === 'FAILED' ? 'Retry Send' : 'Send Now'}
          </MenuItem>
        )}
        {selectedEmail?.status === 'DRAFT' && (
          <MenuItem onClick={() => { handleEdit(selectedEmail); handleMenuClose(); }}>
            <ScheduleIcon fontSize="small" sx={{ mr: 1 }} /> Schedule
          </MenuItem>
        )}
        {selectedEmail?.status === 'SCHEDULED' && (
          <MenuItem onClick={handleCancelClick}>
            <CancelIcon fontSize="small" sx={{ mr: 1 }} /> Cancel Schedule
          </MenuItem>
        )}
        {(selectedEmail?.status === 'SENT' || selectedEmail?.status === 'SENDING') && (
          <MenuItem component={Link} to={`/${schoolSlug}/admin/custom-emails/${selectedEmail?.id}/analytics`} onClick={handleMenuClose}>
            <AnalyticsIcon fontSize="small" sx={{ mr: 1 }} /> Analytics
          </MenuItem>
        )}
        {(selectedEmail?.status === 'DRAFT' || selectedEmail?.status === 'FAILED') && (
          <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>

      {/* Send Confirmation Dialog.
          ⚠️ The count MUST come from the live recipient preview, not from
          `emailToSend.totalRecipients`. That column is only written AFTER a send
          completes, so on a DRAFT it is always 0 — the dialog told the admin
          they were about to email "0 recipients" on every genuine first send,
          which is the one moment the number actually matters. The editor page
          was already using this hook, which is why it showed the right number
          and the list page did not.

          Sending is blocked until the count resolves: confirming against an
          unknown audience is exactly what this dialog exists to prevent. */}
      <ConfirmDialog
        open={sendDialogOpen}
        title="Send Email Campaign"
        message={
          isLoadingSendPreview
            ? `Checking how many recipients "${emailToSend?.title}" will reach…`
            : sendPreviewError
              ? `Could not work out how many recipients "${emailToSend?.title}" will reach. Open the campaign to check its audience before sending.`
              : emailToSend?.status === 'FAILED'
                ? // A retry is NOT a fresh send to everyone — saying "to N
                  // recipients" here would imply the whole audience is being
                  // emailed again, which is the thing admins are most afraid of.
                  `Retry "${emailToSend?.title}"? Only recipients who did not receive it will be emailed — anyone already delivered is skipped.`
                : `Are you sure you want to send "${emailToSend?.title}" to ${
                    sendPreview?.total ?? 0
                  } recipients? This action cannot be undone.`
        }
        confirmLabel={emailToSend?.status === 'FAILED' ? 'Retry' : 'Send'}
        confirmColor="primary"
        onConfirm={handleSendConfirm}
        onCancel={() => {
          setSendDialogOpen(false);
          setEmailToSend(null);
        }}
        loading={sendMutation.isPending || isLoadingSendPreview}
        confirmDisabled={!!sendPreviewError || (sendPreview?.total ?? 0) === 0}
      />

      {/* Cancel Confirmation Dialog */}
      <ConfirmDialog
        open={cancelDialogOpen}
        title="Cancel Scheduled Email"
        message={`Are you sure you want to cancel the scheduled sending of "${emailToCancel?.title}"? The email will be moved back to draft status.`}
        confirmLabel="Cancel Schedule"
        confirmColor="warning"
        onConfirm={handleCancelConfirm}
        onCancel={() => {
          setCancelDialogOpen(false);
          setEmailToCancel(null);
        }}
        loading={cancelMutation.isPending}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Custom Email"
        message={`Are you sure you want to delete "${emailToDelete?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        confirmColor="error"
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setDeleteDialogOpen(false);
          setEmailToDelete(null);
        }}
        loading={deleteMutation.isPending}
      />
    </Box>
  );
}
